import { z } from 'zod';

/**
 * Registry of free-text-parseable shapes, keyed by a Prisma-compatible model
 * name. A single abstracted input (Customer/Admin) and a single OpenAI-backed
 * procedure are driven by this registry: the caller passes a `type` and gets
 * back a structured value matching the corresponding Prisma model, plus a
 * normalized newline-separated string for display.
 *
 * To support a new shape, add a Zod schema, its editable fields, an extraction
 * prompt, and a formatter here — no changes to the component or procedure.
 */

// ─── Contact (mirrors the editable subset of the Prisma `Contact` model) ────────
// Every key here MUST be a real `Contact` column: the shipment-form service spreads
// this value straight into a Prisma Contact create/upsert (see shipment-form.ts).
// The address is a nested object stored in the `Contact.address` JSON column — the
// same `Address` shape as `Airport.address` — so both share the mapping helpers.
export const structuredAddressSchema = z.object({
  recipient: z.string().nullish(),
  line_one: z.string().nullish(),
  line_two: z.string().nullish(),
  city: z.string().nullish(),
  state: z.string().nullish(),
  zip_code: z.string().nullish(),
  country: z.string().nullish(),
});
export type StructuredAddress = z.infer<typeof structuredAddressSchema>;

export const structuredContactSchema = z.object({
  full_name: z.string().nullish(),
  title: z.string().nullish(),
  phone_number: z.string().nullish(),
  email: z.string().nullish(),
  address: structuredAddressSchema.nullish(),
  notes: z.string().nullish(),
});
export type StructuredContact = z.infer<typeof structuredContactSchema>;

// ─── Registry ───────────────────────────────────────────────────────────────────
export const PARSABLE_TYPE_VALUES = ['Contact'] as const;
export const parsableTypeSchema = z.enum(PARSABLE_TYPE_VALUES);
export type ParsableType = (typeof PARSABLE_TYPE_VALUES)[number];

export type ParsableFieldDef = {
  key: string;
  label: string;
  placeholder?: string;
  multiline?: boolean;
};

type ParsableConfig = {
  /** Validates the structured value returned by the parser. */
  schema: z.ZodTypeAny;
  /** Ordered fields for the "Manual edit" UI (and the LLM extraction target). */
  fields: ParsableFieldDef[];
  /** Field guidance injected into the extraction prompt. */
  prompt: string;
  /** Normalize a parsed value into newline-separated display text. */
  format: (value: any) => string;
};

const joinLines = (values: (string | null | undefined)[]): string =>
  values
    .map((value) => (value ?? '').trim())
    .filter(Boolean)
    .join('\n');

/** "City, State Zip" from a structured address — omitting whichever parts are blank. */
const cityStateLine = (address?: StructuredAddress | null): string => {
  const cityState = [address?.city, address?.state]
    .map((part) => (part ?? '').trim())
    .filter(Boolean)
    .join(', ');
  return [cityState, (address?.zip_code ?? '').trim()].filter(Boolean).join(' ');
};

export const PARSABLE_TYPES: Record<ParsableType, ParsableConfig> = {
  Contact: {
    schema: structuredContactSchema,
    fields: [
      { key: 'full_name', label: 'Name / Company', placeholder: 'Acme Corp' },
      { key: 'title', label: 'Title', placeholder: 'Logistics Manager' },
      { key: 'phone_number', label: 'Phone', placeholder: '+1 555 123 4567' },
      { key: 'email', label: 'Email', placeholder: 'ops@acme.com' },
      { key: 'address.line_one', label: 'Address line 1', placeholder: '123 Main St' },
      { key: 'address.line_two', label: 'Address line 2', placeholder: 'Suite 400' },
      { key: 'address.city', label: 'City', placeholder: 'Los Angeles' },
      { key: 'address.state', label: 'State / Province', placeholder: 'CA' },
      { key: 'address.zip_code', label: 'Postal code', placeholder: '90045' },
      { key: 'address.country', label: 'Country', placeholder: 'United States' },
      { key: 'notes', label: 'Notes', placeholder: 'Any other details', multiline: true },
    ],
    prompt: [
      'full_name (person or company name)',
      'title (job title or role, if present)',
      'phone_number',
      'email',
      'address (a nested object with snake_case keys: line_one, line_two, city, state, zip_code, country)',
      'notes (any other free-form details not captured by the fields above)',
    ].join('\n- '),
    format: (value: StructuredContact) =>
      joinLines([
        value?.full_name,
        value?.title,
        value?.phone_number,
        value?.email,
        value?.address?.line_one,
        value?.address?.line_two,
        cityStateLine(value?.address),
        value?.address?.country,
        value?.notes,
      ]),
  },
};

/** Normalize a parsed value of the given type into newline-separated text. */
export const formatParsable = (type: ParsableType, value: unknown): string =>
  PARSABLE_TYPES[type].format(value ?? {});

/** Ordered editable fields for the given type (drives the manual-edit UI). */
export const getParsableFields = (type: ParsableType): ParsableFieldDef[] => PARSABLE_TYPES[type].fields;
